import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import api from './api/axiosInstance';
import { Form, Row, Col, Button, Card, Modal, Image, Table } from 'react-bootstrap';
import icNoInsights from './assets/img/ic-empty-box.svg';
import CityInsightsFilter from './CityInsightsFilter';
import Pagination from 'react-bootstrap/Pagination';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import icValidationError from './assets/img/ic-danger.svg';
import icSucess from './assets/img/ic-celebration-circle.svg';

const CityUniversities = forwardRef(({ errors }, ref) => {

    useImperativeHandle(ref, () => ({ openModal: () => setShowUniversityModal(true) }));
    const { id } = useParams();
    const [universities, setUniversities] = useState([]); 
    const [filteredUniversities, setFilteredUniversities] = useState([]);
    const navigate = useNavigate();
    const [showUniversityModal, setShowUniversityModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [citiesPerPage, setCitiesPerPage] = useState(10);

    
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentUniversities = filteredUniversities.slice(indexOfFirst, indexOfLast);

    const [cities, setCities] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);

    const indexOfLastCity = currentPage * citiesPerPage;
    const indexOfFirstCity = indexOfLastCity - citiesPerPage;
    const currentCities = filteredCities.slice(indexOfFirstCity, indexOfLastCity);
   
    const [showDeleteUniversityModal, setShowDeleteUniversityModal] = useState(false); // Delete modal state
    const [toDelete, setToDelete] = useState({ cityId: null, universityId: null });
    const [saving, setSaving] = useState({}); // universityId -> boolean (disable toggle while saving)

    const [selected, setSelected] = useState(null);
    
    const handleLocalChange = (e) => {
        const { name, type, checked, value } = e.target;

        // strip known prefixes (supports both forms)
        const key = name
            .replace(/^cityInsight\./, '')
            .replace(/^university\./, '');

        // base value from control type
        let v = type === 'checkbox' ? checked : value;

        // light coercion for numeric fields
        const numberKeys = new Set(['googleRating', 'studentsNumber']);
        if (numberKeys.has(key)) {
            v = v === '' ? '' : Number(v);
        }

        setSelected(prev => ({ ...(prev || {}), [key]: v }));
    };

    const [touched, setTouched] = useState({ 
        universityUrl: false
    });

    const [localErrors, setLocalErrors] = useState({
        universityUrl: ''
    });

    const validateUniversityUrl = (url) => {
        if (!url) return 'URL is required';
        try {
            const u = new URL(url);
            if (!/^https?:$/.test(u.protocol)) return 'URL must start with http:// or https://';
            return '';
        } catch {
            return 'Enter a valid URL';
        }
    };

    const handleUniversityURLBlur = () => {
        setTouched((t) => ({ ...t, universityUrl: true }));
        const msg = validateUniversityUrl(selected?.url ?? ''); // ✅ check the actual field
        setLocalErrors((e) => ({ ...e, universityUrl: msg }));
    };

    const universityDefaults = {
        name: '',
        address: '',
        telephone: '',
        url: '',
        classification: '',
        studentsNumber: null, // number; use (selected?.studentsNumber ?? '') in inputs
    };
    
    const hasAnyUniversity = (u) => {
        if (!u) return false;
        const entries = [
            u.name, u.address, u.telephone, u.url, u.classification,
            (u.studentsNumber ?? '') !== '' ? String(u.studentsNumber) : ''
        ];
        return entries.some(v => String(v || '').trim().length > 0);
    };

    const normalizeUniversities = (city) => {
        const arr = Array.isArray(city.universities)
            ? city.universities
            : (city.university ? [city.university] : []); // backward compat (if any)

        return arr

        .map((u) => ({
            ...universityDefaults,
            ...u,
            studentsNumber: (u.studentsNumber === '' || u.studentsNumber == null)
                ? ''
                : Number(u.studentsNumber),
            _id: u._id || undefined,
        }))
        .filter(hasAnyUniversity);
    };

    const fetchUniversities = useCallback(async () => {
        try {
            const res = await api.get(`/api/cities/${id}`);
            const city = res.data || {};
            const list = normalizeUniversities(city);
            setUniversities(list);
            setFilteredUniversities(list);
            setCurrentPage(1);
        } catch (error) {
            console.error('Error fetching universities:', error);
        }
    }, [id]);

    useEffect(() => {
        fetchUniversities();
    }, [fetchUniversities]);

    const [searchQuery, setSearchQuery] = useState('');

    const [sort, setSort] = useState({ by: null, dir: 'asc' });

    // search filter
    useEffect(() => {
        const q = searchQuery.trim().toLowerCase();
        const next = universities.filter((ci) => {
            if (!q) return true;
            return [ci.service, ci.name, ci.url]
            .map(v => String(v || '').toLowerCase())
            .some(s => s.includes(q));
        });
        setFilteredUniversities(applySort(next, sort));
        setCurrentPage(1);
    }, [searchQuery, universities]);

    const applySort = (arr, { by, dir }) => {
        if (!by) return arr;
        const mul = dir === 'desc' ? -1 : 1;
        return [...arr].sort((a, b) => {
            const pick = (key, obj) => {
            switch (key) {
                case 'service':                     return (obj.service || '').toLowerCase().trim();
                case 'name':                        return (obj.name || '').toLowerCase().trim();
                case 'url':                         return (obj.url || '').toLowerCase().trim();
                case 'providerUrl':      return (obj.url || '').toLowerCase().trim();
                case 'rating':                      return parseFloat(obj.googleRating || '0') || 0;
                case 'show':                        return obj.showStatus ? 1 : 0;
                default:                            return '';
            }
            };
            const va = pick(by, a);
            const vb = pick(by, b);
            if (va < vb) return -1 * mul;
            if (va > vb) return  1 * mul;
            return 0;
        });
    };

    const toggleSort = (by) => {
        setSort(prev => {
            const dir = prev.by === by ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'asc';
            const next = { by, dir };
            setFilteredUniversities(applySort(filteredUniversities, next));
            return next;
        });
    };

    const toggleShowStatus = useCallback(
        async (universityId, next) => {
            
            setUniversities(prev =>
                prev.map(i => (i._id === universityId ? { ...i, showStatus: next } : i))
            );

            setFilteredUniversities(prev =>
                prev.map(i => (i._id === universityId ? { ...i, showStatus: next } : i))
            );

            setSaving(s => ({ ...s, [universityId]: true }));

            try {
                const res = await api.patch(
                    `/api/cities/${id}/universities/${universityId}`,
                    { showStatus: next }
                );
                // rebuild from server truth (expects updated city payload)
                const updated = normalizeUniversities(res.data || {});
                setUniversities(updated);
                setFilteredUniversities(updated);
            } catch (err) {
            // revert on error
            setUniversities(prev =>
                prev.map(i => (i._id === universityId ? { ...i, showStatus: !next } : i))
            );
            setFilteredUniversities(prev =>
                prev.map(i => (i._id === universityId ? { ...i, showStatus: !next } : i))
            );
            console.error('Error toggling showStatus:', err);
            alert('Could not update Show status. Please try again.');
            } finally {
                setSaving(s => {
                    const { [universityId]: _, ...rest } = s;
                    return rest;
                });
            }
        },
        [id]
    );


    // url normalize for anchor
    const safeHref = (raw) => {
        if (!raw) return null;
        try {
            const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(raw) ? raw : `https://${raw}`;
            const u = new URL(withScheme);
        return ['http:', 'https:'].includes(u.protocol) ? u.toString() : null;
        } catch {
            return null;
        }
    };

    const noUniversities = filteredUniversities.length === 0;

    const handleDeleteUniversity = () => {
        setShowDeleteUniversityModal(true);
    };

    const confirmDeleteUniversity = async (cityId, universityId) => {
        try {
            const res = await api.delete(`/api/cities/${cityId}/universities/${universityId}`);
            // Update UI: remove from local state
            setCities(prev => prev.map(c => c._id === cityId ? { ...c, cityInsights: res.data.cityInsights } : c));
            setFilteredCities(prev => prev.map(c => c._id === cityId ? { ...c, cityInsights: res.data.cityInsights } : c));
        } catch (err) {
            console.error('Error deleting insight:', err);
        }
    };

    const totalPages = Math.ceil(filteredUniversities.length / rowsPerPage);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col>
                            <h3 className="mb-0">Universities</h3>
                        </Col>
                        <Col md={6} className="text-end">
                            <Button onClick={() => { setSelected({ ...universityDefaults }); setShowUniversityModal(true); }} variant="outline-secondary" className="btn-sm">
                                Add New University
                            </Button>
                        </Col> 
                    </Row>
                    <hr className="pt-4" />
                     <Row>
                        <Col xs={12}>
                            <CityInsightsFilter
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                universities={universities}
                            />
                        </Col>
                    </Row>

                    {noUniversities ? (
                        <Row>
                            <Col md={12}>
                                <Row className="mb-3">
                                    <Col md={6} className="no-result offset-md-3 d-flex flex-column justify-content-center align-items-center text-center">
                                        <Image src={icNoInsights} alt="No City universities" className="mb-4" />
                                        <h2 className="mb-3">No city universities yet.</h2>
                                        <p className="mb-4">Your city universities will be displayed here.</p>
                                        <Button onClick={() => { setSelected({ ...universityDefaults }); setShowUniversityModal(true); }} variant="primary">Add city universities</Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row>
                                <Col>
                                    <div className="table-responsive list">
                                        <Table>
                                            <thead>
                                                <tr>
                                                    <th onClick={() => toggleSort('name')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                            University Name
                                                            <span>
                                                                <FaCaretUp className={sort.by === 'name' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                <FaCaretDown className={sort.by === 'name' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                            </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('address')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                            Address
                                                            <span>
                                                                <FaCaretUp className={sort.by === 'address' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                <FaCaretDown className={sort.by === 'address' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                            </span>
                                                        </div>
                                                    </th>                                                    
                                                    <th onClick={() => toggleSort('telephone')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                            Telephone
                                                            <span>
                                                                <FaCaretUp className={sort.by === 'telephone' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                <FaCaretDown className={sort.by === 'telephone' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                            </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('url')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                            Website
                                                            <span>
                                                                <FaCaretUp className={sort.by === 'url' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                <FaCaretDown className={sort.by === 'url' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                            </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('clasification')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                            Clasification
                                                            <span>
                                                                <FaCaretUp className={sort.by === 'clasification' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                <FaCaretDown className={sort.by === 'clasification' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                            </span>
                                                        </div>
                                                    </th>
                                                    <th onClick={() => toggleSort('studentsNumber')} style={{ cursor: 'pointer' }}>
                                                        <div>
                                                            Number of students
                                                            <span>
                                                                <FaCaretUp className={sort.by === 'studentsNumber' && sort.dir === 'asc' ? 'text-primary' : ''} />
                                                                <FaCaretDown className={sort.by === 'studentsNumber' && sort.dir === 'desc' ? 'text-primary' : ''} />
                                                            </span>
                                                        </div>
                                                    </th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentUniversities.map((ins) => {
                                                    const href = (() => {
                                                    if (!ins.url) return null;
                                                    try {
                                                        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(ins.url) ? ins.url : `https://${ins.url}`;
                                                        const u = new URL(withScheme);
                                                        return ['http:', 'https:'].includes(u.protocol) ? u.toString() : null;
                                                    } catch { return null; }
                                                    })();

                                                    return (
                                                        <tr key={ins._id || `${ins.name}.${ins.service}`}>
                                                            <td>{ins.name || 'N/A'}</td>
                                                            <td>{ins.address || 'N/A'}</td>
                                                            <td>{ins.telephone || 'N/A'}</td>
                                                            <td>{href ? <a href={href} target="_blank" rel="noopener noreferrer">{ins.url}</a> : '—'}</td>
                                                            <td>{ins.classification || 'N/A'}</td>
                                                            <td>{ins.studentsNumber || 'N/A'}</td>
                                                            <td className="text-end">
                                                                <Button
                                                                    className="btn-sm"
                                                                    variant="outline-secondary"
                                                                    onClick={() => {
                                                                        setSelected(ins);
                                                                        setShowUniversityModal(true);
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    className="btn-sm ms-2"
                                                                    onClick={() => {
                                                                        setToDelete({ universityId: ins._id });
                                                                        setShowDeleteUniversityModal(true);
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>

                                        </Table>
                                    </div>
                                </Col>
                            </Row>
                            {/* Pagination Row with Total Count, Items Per Page Selector & "Go to Page" Input */}
                            <Row className="pagination-wrapper">
                                <Col className="d-flex align-items-center">
                                    <span>Total&nbsp;{filteredUniversities.length}&nbsp;universities</span>
                                    <Pagination>
                                        <Pagination.Prev 
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                            disabled={currentPage === 1}
                                        />
                                        {[...Array(totalPages).keys()].map(number => (
                                            <Pagination.Item 
                                                key={number + 1} 
                                                active={number + 1 === currentPage} 
                                                onClick={() => handlePageChange(number + 1)}
                                            >
                                                {number + 1}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next 
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </Col>
                                <Col className="pagination-controls">
                                    <div className="pagination-controls-per-page">
                                        <Form.Select
                                            value={rowsPerPage}
                                            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                                            style={{ width: 'auto', marginRight: '10px' }}
                                        >
                                            <option value="10">10 / page</option>
                                            <option value="20">20 / page</option>
                                            <option value="40">40 / page</option>
                                        </Form.Select>
                                    </div>
                                    <label className="pagination-controls-goto-label" htmlFor="goto-page">
                                        Go to
                                    </label>
                                    <Form.Control
                                        type="number"
                                        className="pagination-controls-goto-input"
                                        value={currentPage}
                                        onChange={(e) => {
                                            const newPage = Number(e.target.value);
                                            if (newPage >= 1 && newPage <= totalPages) {
                                                setCurrentPage(newPage); // ✅ Only valid pages
                                            }
                                        }}
                                        onBlur={() => {
                                            if (currentPage < 1) setCurrentPage(1);
                                            if (currentPage > totalPages) setCurrentPage(totalPages);
                                        }}
                                        style={{ textAlign: 'center' }}
                                    />
                                    
                                </Col>
                            </Row>
                            
                        </>
                    )}
                </Card.Body>
            </Card>

            <Modal className="with-form" show={showUniversityModal} onHide={() => { setShowUniversityModal(false); setSelected({ ...universityDefaults }); }} centered>
                <Form
                    onSubmit={async (e) => {
                        e.preventDefault();
                        setSelected({ ...universityDefaults });
                        try {
                        const payload = {
                            name:           selected?.name ?? '',
                            address:        selected?.address ?? '',
                            telephone:      selected?.telephone ?? '',
                            url:            selected?.url ?? '',
                            classification: selected?.classification ?? '',
                            studentsNumber: selected?.studentsNumber ?? null,
                        };

                        if (selected?._id) {
                            await api.patch(
                                `/api/cities/${id}/universities/${selected._id}`,
                                payload
                            );
                        } else {
                            await api.post(
                                `/api/cities/${id}/universities`,
                                payload
                            );
                        }

                        await fetchUniversities(); // refresh list
                            setShowUniversityModal(false);
                            setShowSuccessModal(true);
                        } catch (err) {
                            console.error('Save university failed:', err);
                            alert('Could not save the university. Please try again.');
                        }
                    }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>{selected?._id ? 'Edit University' : 'Add New University'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>University name</Form.Label>
                                    <Form.Control
                                    type="text"
                                    value={selected?.name ?? ''}
                                    onChange={(e) => handleLocalChange({ target: { name: 'name', value: e.target.value }})} // ✅ map to selected.name
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>  
                                <Form.Group className="mb-3">
                                    <Form.Label>Address</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selected?.address ?? ''}
                                        onChange={(e) => handleLocalChange({ target: { name: 'address', value: e.target.value }})} // ✅ selected.address
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>  
                                <Form.Group className="mb-3">
                                    <Form.Label>Telephone</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selected?.telephone ?? ''}
                                        onChange={(e) => handleLocalChange({ target: { name: 'telephone', value: e.target.value }})} // ✅ selected.telephone
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="validated mb-3" controlId="universityUrl"> {/* keep validated + isInvalid */}
                                    <Form.Label>Website URL</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={selected?.url ?? ''}
                                        onChange={(e) => handleLocalChange({ target: { name: 'url', value: e.target.value }})}
                                        onBlur={handleUniversityURLBlur} // ✅ new blur handler
                                        placeholder="example.com"
                                        inputMode="url"
                                        isInvalid={touched.universityUrl && !!(localErrors?.universityUrl || errors?.universityUrl)}
                                        aria-describedby="universityUrlFeedback"
                                    />
                                    {!!(localErrors?.universityUrl || errors?.universityUrl) && (
                                        <div className="custom-error-icon-wrapper">
                                            <img
                                                src={icValidationError}
                                                alt={localErrors?.universityUrl || errors?.universityUrl}
                                                className="custom-error-icon"
                                            />
                                            <div className="custom-tooltip">
                                                {localErrors?.universityUrl || errors?.universityUrl}
                                            </div>
                                        </div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Classification</Form.Label>
                                    <Form.Select
                                        value={selected?.classification ?? ''}
                                        onChange={(e) =>
                                            handleLocalChange({
                                                target: { name: 'classification', value: e.target.value },
                                            })
                                        } // ✅ selected.classification
                                    >
                                        <option value="" disabled>Select classification</option> {/* optional placeholder */}
                                        <option value="Full Universities">Full Universities</option>
                                        <option value="Universities of Applied Sciences (UAS) / Polytechnics">
                                            Universities of Applied Sciences (UAS) / Polytechnics
                                        </option>
                                        <option value="Specialized Universities">Specialized Universities</option>
                                        <option value="Adult Education">Adult Education</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col>
                                <Form.Group className="mb-3">
                                    <Form.Label>Number of students</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={selected?.studentsNumber ?? ''}
                                        onChange={(e) => handleLocalChange({ target: { name: 'studentsNumber', value: Number(e.target.value) || 0 }})} // ✅ selected.studentsNumber
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => { setShowUniversityModal(false); setSelected({ ...universityDefaults }); }}>Cancel</Button>
                        <Button variant="primary" className="ms-3" type="submit">Save</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
            


            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteUniversityModal} onHide={() => setShowDeleteUniversityModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this University? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteUniversityModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={async () => {
                            if (!toDelete.universityId) return;
                            try {
                            const res = await api.delete(`/api/cities/${id}/universities/${toDelete.universityId}`);

                            // Rebuild the local list from the updated city payload
                            const updated = normalizeUniversities(res.data || {});
                            setUniversities(updated);

                            setFilteredUniversities(
                                applySort(
                                updated.filter(ci => {
                                    const q = searchQuery.trim().toLowerCase();
                                    if (!q) return true;
                                    return [ci.service, ci.name, ci.url]
                                    .map(v => String(v || '').toLowerCase())
                                    .some(s => s.includes(q));
                                }),
                                sort
                                )
                            );
                            } catch (err) {
                                console.error('Error deleting insight:', err);
                                alert('An error occurred while deleting the city insight.');
                            } finally {
                                setShowDeleteUniversityModal(false);
                                setToDelete({ universityId: null });
                            }
                        }}
                    >
                            Delete
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">University successfully created</h2>
                    <p className="text-center">I would like to confirm that the university has been successfully created in the system.</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button variant="primary" onClick={() => navigate('/onboarding-city-list')}>
                        Go back to cities
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
});

export default CityUniversities;
